import { createStitches } from "@stitches/react";
export * from "@stitches/react";
function createSpaces() {
  const spaces: { [key: number]: string } = {};
  let i = 1;
  while (i < 16) {
    spaces[i] = `${i * 4}px`;
    i++;
  }
  return spaces;
}

const theme = {
  colors: {
    "shadow-sm": "Black",
    teal900: "#017782",
    teal700: "#0199A7",
    teal600: "#01AAB9",
    teal500: "#1AB3C0",
    teal400: "#85D8DE",
    teal300: "#BAE8ED",
    teal200: "#D7F1F4",
    teal100: "#E4F6F7",
    teal50: "#F2FAFB",
    magenta700: "#E62E70",
    magenta600: "#FF337C",
    magenta500: "#FF4789",
    magenta400: "#FF9EC2",
    magenta300: "#FFCEE0",
    magenta200: "#FFDFEA",
    magenta100: "#FFE8F0",
    gray900: "#0A2545",
    gray800: "#233B58",
    gray700: "#3B516A",
    gray600: "#637488",
    gray500: "#6B7280",
    gray400: "#9CA3AF",
    gray300: "#D1D5DB",
    gray200: "#E5E7EB",
    gray100: "#F3F4F6",
    gray50: "#F9FAFB",
    red50: "rgb(254 242 242)",
    red100: "#FFEBEC",
    red200: "#FFDFE1",
    red300: "rgb(252 165 65)",
    red400: "rgb(248 113 113)",
    red500: "rgb(239 68 68)",
    red600: "rgb(255, 51, 63)",
    red700: "#E62E39",
    red800: "#CC2932",
    lime100: "#EAF7ED",
    lime200: "#E1F3E5",
    lime300: "#D2EED8",
    lime400: "#A5DDB2",
    lime500: "#56BE6D",
    lime600: "#43B75D",
    lime700: "#36924A",
    lime800: "#36924A",
    sunshine50: "#FFFAED",
    sunshine100: "#FEF5E1",
    sunshine200: "#FFEFCC",
    sunshine500: "#FDC443",
    sunshine600: `#E69A00`,
    sunshine700: "#E69A00",
    violet100: "#F0EDFB",
    violet200: "#E7E2F9",
    violet600: "#5E41BF",
    violet700: "#5E41BF",
    violet800: "#533AAA",
    mango100: "#FFEFEA",
    mango200: "#FFE1D8",
    mango600: "#FF5722",
    mango700: "#E64E1F",
    ocean100: "#EEF5FE",
    ocean500: "",
    ocean600: "#2278ED",
    ocean700: "#1F6CD5",
  },
  space: createSpaces(),
  fontSizes: {
    1: "12px",
    2: "14px",
    3: "16px",
    4: "18px",
    5: "20px",
    6: "22px",
    7: "24px",
  },
  fonts: {
    inter: "Inter, -apple-system, system-ui, sans-serif",
    opensan: "OpenSans, -apple-system, system-ui, sans-serif",
    larsseit: "Larsseit, -apple-system, system-ui, sans-serif",
  },
  radii: {
    1: "4px",
    2: "6px",
    3: "8px",
    4: "16px",
    round: "50%",
    full: "9999px",
  },
  zIndices: {
    1: "10",
    2: "20",
    3: "30",
    4: "50",
    5: "100",
    6: "1000",
    7: "1001",
  },
  lineHeights: {
    1: "16px",
    2: "20px",
    3: "24px",
    4: "28px",
    5: "32px",
  },
  shadows: {
    "shadow-md": "0 0 0 1px rgb(0 0 0 / 0.10), 0 0 0 2px rgb(0 0 0 / 0.05)",
  },
} as const;

const { styled } = createStitches({
  theme,
  utils: {
    p: (value: any) => ({
      paddingTop: value,
      paddingBottom: value,
      paddingLeft: value,
      paddingRight: value,
    }),
    pt: (value: any) => ({
      paddingTop: value,
    }),
    pr: (value: any) => ({
      paddingRight: value,
    }),
    pb: (value: any) => ({
      paddingBottom: value,
    }),
    pl: (value: any) => ({
      paddingLeft: value,
    }),
    px: (value: any) => ({
      paddingLeft: value,
      paddingRight: value,
    }),
    py: (value: any) => ({
      paddingTop: value,
      paddingBottom: value,
    }),
    m: (value: any) => ({
      marginTop: value,
      marginBottom: value,
      marginLeft: value,
      marginRight: value,
    }),
    mt: (value: any) => ({
      marginTop: value,
    }),
    mr: (value: any) => ({
      marginRight: value,
    }),
    mb: (value: any) => ({
      marginBottom: value,
    }),
    ml: (value: any) => ({
      marginLeft: value,
    }),
    mx: (value: any) => ({
      marginLeft: value,
      marginRight: value,
    }),
    my: (value: any) => ({
      marginTop: value,
      marginBottom: value,
    }),
    ta: (value: any) => ({ textAlign: value }),
    fd: (value: any) => ({ flexDirection: value }),
    fw: (value: any) => ({ flexWrap: value }),
    ai: (value: any) => ({ alignItems: value }),
    ac: (value: any) => ({ alignContent: value }),
    jc: (value: any) => ({ justifyContent: value }),
    as: (value: any) => ({ alignSelf: value }),
    fg: (value: any) => ({ flexGrow: value }),
    fs: (value: any) => ({ flexShrink: value }),
    fb: (value: any) => ({ flexBasis: value }),
    row: (value: any) => ({ display: "flex", flexDirection: "row" }),
    bc: (value: keyof typeof theme["colors"] | (string & {})) => ({
      backgroundColor: value,
    }),

    br: (value: keyof typeof theme["radii"] | (string & {})) => ({
      borderRadius: value,
    }),
    btrr: (value: keyof typeof theme["radii"] | (string & {})) => ({
      borderTopRightRadius: value,
    }),
    bbrr: (value: keyof typeof theme["radii"] | (string & {})) => ({
      borderBottomRightRadius: value,
    }),
    bblr: (value: keyof typeof theme["radii"] | (string & {})) => ({
      borderBottomLeftRadius: value,
    }),
    btlr: (value: keyof typeof theme["radii"] | (string & {})) => ({
      borderTopLeftRadius: value,
    }),

    bs: (value: any) => ({ boxShadow: value }),

    lh: (value: any) => ({ lineHeight: value }),

    ox: (value: any) => ({ overflowX: value }),
    oy: (value: any) => ({ overflowY: value }),

    pe: (value: any) => ({ pointerEvents: value }),
    us: (value: any) => ({ userSelect: value }),

    // size: (value: keyof typeof theme['sizes'] | (string & {})) => ({
    //   width: value,
    //   height: value,
    // }),

    linearGradient: (value: any) => ({
      backgroundImage: `linear-gradient(${value})`,
    }),
    ring: (value: any) => ({
      boxShadow: "$$offsetShadow, $$ringShadow, $$ringShadow",
    }),
  },
});

export { styled };
